/**
 * Cloudflare Stream helpers.
 *
 * Admin actions (upload / list / status / delete) call the admin-only
 * `cloudflare-stream` edge function with the signed-in admin's JWT. Video
 * bytes always upload DIRECTLY from the browser to Cloudflare — never through
 * our servers.
 *
 * Public pages render a player from a UID stored in page content, using the
 * customer subdomain below (project default; overridable at runtime once an
 * admin has saved a different one).
 */

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

/** Default Cloudflare Stream customer subdomain for this project. */
export const DEFAULT_STREAM_SUBDOMAIN = 'customer-qqdvjs7hy46whegj.cloudflarestream.com';

// Runtime-overridable subdomain (set once the admin settings load).
let activeSubdomain = DEFAULT_STREAM_SUBDOMAIN;

export function setStreamSubdomain(subdomain?: string | null): void {
  if (subdomain && subdomain.trim()) activeSubdomain = subdomain.trim();
}

export function getStreamSubdomain(): string {
  return activeSubdomain || DEFAULT_STREAM_SUBDOMAIN;
}

export interface StreamVideoRow {
  uid: string;
  title?: string | null;
  duration?: number | null;
  thumbnail?: string | null;
  status?: string | null;
  size_bytes?: number | null;
  captions_en?: boolean | null;
  created_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

/** Shape returned by the `list` / `sync` actions. */
export interface StreamListResult {
  ok: boolean;
  videos: StreamVideoRow[];
  source?: 'cloudflare' | 'local';
  synced?: number;
  total?: number;
  subdomain?: string | null;
  warning?: string;
}

export interface StreamCallResult<T = any> {
  ok: boolean;
  data: T;
  error?: string;
}

function adminToken(): string {
  const t = (typeof window !== 'undefined' && (window as any).getCendienToken)
    ? (window as any).getCendienToken()
    : '';
  return t || SUPABASE_ANON_KEY;
}

/** Calls the admin-only cloudflare-stream edge function. */
export async function streamCall<T = any>(action: string, payload: Record<string, unknown> = {}): Promise<StreamCallResult<T>> {
  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/cloudflare-stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminToken()}`,
        apikey: SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ action, ...payload }),
    });
    const data = (await res.json().catch(() => null)) as any;
    if (!res.ok) {
      return { ok: false, data, error: data?.error || `Request failed (HTTP ${res.status})` };
    }
    return { ok: true, data };
  } catch (e) {
    return { ok: false, data: null, error: `Could not reach the video service: ${(e as Error).message}` };
  }
}

// ── URL builders ────────────────────────────────────────────────────────────
export function streamThumbnail(uid: string, subdomain?: string, opts?: { time?: string; height?: number }): string {
  const host = subdomain || getStreamSubdomain();
  const time = opts?.time || '2s';
  const height = opts?.height || 720;
  return `https://${host}/${uid}/thumbnails/thumbnail.jpg?time=${time}&height=${height}`;
}

export interface StreamIframeOptions {
  subdomain?: string;
  poster?: string | null;
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
  captions?: boolean;
  preload?: boolean;
}

export function streamIframeUrl(uid: string, opts: StreamIframeOptions = {}): string {
  const host = opts.subdomain || getStreamSubdomain();
  const params = new URLSearchParams();
  if (opts.poster) params.set('poster', opts.poster);
  const captions = opts.captions !== false;
  if (captions) params.set('defaultTextTrack', 'en');
  if (opts.autoplay) params.set('autoplay', 'true');
  const muted = opts.muted !== false; // default muted so autoplay is allowed
  if (muted) params.set('muted', 'true');
  if (opts.loop) params.set('loop', 'true');
  if (opts.controls === false) params.set('controls', 'false');
  params.set('preload', opts.preload === false ? 'none' : 'metadata');
  const qs = params.toString();
  return `https://${host}/${uid}/iframe${qs ? `?${qs}` : ''}`;
}

export function streamHlsUrl(uid: string, subdomain?: string): string {
  const host = subdomain || getStreamSubdomain();
  return `https://${host}/${uid}/manifest/video.m3u8`;
}

/** Ready-to-paste <iframe> embed markup. */
export function streamEmbedCode(uid: string, opts: StreamIframeOptions = {}): string {
  const src = streamIframeUrl(uid, { ...opts, autoplay: false });
  const title = opts.poster ? 'Cendien video' : 'Cendien video';
  return `<iframe src="${src}" title="${title}" loading="lazy" allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture" allowfullscreen style="border:0;width:100%;aspect-ratio:16/9;"></iframe>`;
}

// ── Uploads (browser → Cloudflare, direct) ─────────────────────────────────
export interface StartUploadResult {
  mode: 'direct' | 'tus';
  uid: string;
  uploadURL?: string;
  tusEndpoint?: string;
}

export async function startVideoUpload(title: string, file: File): Promise<StreamCallResult<StartUploadResult & { ok?: boolean }>> {
  return streamCall<StartUploadResult & { ok?: boolean }>('create_upload', {
    title,
    fileSize: file.size,
    maxDurationSeconds: 21600,
  });
}

/** POST the file to a one-time Cloudflare direct-upload URL (XHR for progress). */
export function uploadVideoDirect(uploadURL: string, file: File, onProgress: (pct: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const fd = new FormData();
    fd.append('file', file, file.name);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', uploadURL);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`The upload failed (HTTP ${xhr.status}).`));
    };
    xhr.onerror = () => reject(new Error('The upload failed — network error.'));
    xhr.send(fd);
  });
}

/** Resumable (tus) upload for large files, straight to Cloudflare. */
export async function uploadVideoTus(endpoint: string, file: File, onProgress: (pct: number) => void): Promise<void> {
  const tus = await import('tus-js-client');
  return new Promise((resolve, reject) => {
    const upload = new tus.Upload(file, {
      uploadUrl: endpoint,
      chunkSize: 50 * 1024 * 1024,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      metadata: { filename: file.name, filetype: file.type || 'video/mp4' },
      onError: (err: unknown) => reject(err instanceof Error ? err : new Error(String(err))),
      onProgress: (loaded: number, total: number) => {
        if (total > 0) onProgress(Math.round((loaded / total) * 100));
      },
      onSuccess: () => resolve(),
    });
    upload.start();
  });
}

/** Poll a video's processing state until it is ready (or errors / times out). */
export async function pollVideoStatus(uid: string, onTick?: (row: StreamVideoRow) => void, attempts = 25): Promise<StreamVideoRow | null> {
  for (let i = 0; i < attempts; i += 1) {
    const r = await streamCall<{ ok: boolean; video: StreamVideoRow }>('status', { uid });
    const video = r.data?.video;
    if (video) {
      onTick?.(video);
      if (video.status === 'ready' || video.status === 'error') return video;
    }
    await new Promise((res) => setTimeout(res, 3000));
  }
  return null;
}

/** Pulls the whole Cloudflare account into the local library (backfill). */
export async function syncVideos(): Promise<StreamCallResult<{ ok: boolean; synced?: number; total?: number }>> {
  return streamCall<{ ok: boolean; synced?: number; total?: number }>('sync');
}

export function formatStreamDuration(seconds?: number | null): string {
  if (!seconds || !Number.isFinite(seconds)) return '—';
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function formatStreamSize(bytes?: number | null): string {
  if (!bytes || !Number.isFinite(bytes)) return '—';
  const mb = bytes / (1024 * 1024);
  return mb >= 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${mb.toFixed(1)} MB`;
}